/** @jsx jsx */
import React from 'react';
import { styled, keyframes, css, jsx } from '@compiled/react';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';

type LoadingBarProps = {
	itemHeight: number;
	leftPosition: number;
	rightPosition: number;
};

export const LoadingBar = ({ itemHeight, leftPosition, rightPosition }: LoadingBarProps) => (
	<div
		css={[loadingBarBaseStyles, loadingAnimationStyles]}
		// eslint-disable-next-line jira/react/no-style-attribute
		style={{
			height: `${itemHeight - 16}px`,
			left: `${leftPosition}px`,
			right: `${rightPosition}px`,
		}}
	/>
);

const shimmer = keyframes({
	'100%': {
		transform: 'translateX(100%)',
	},
});

const loadingBarBaseStyles = css({
	position: 'absolute',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'flex-end',
	borderRadius: token('border.radius', '3px'),
	overflow: 'hidden',
});

const loadingAnimationStyles = css({
	'&::before, &::after': {
		content: '" "',
		width: '100%',
		height: '100%',
		display: 'block',
	},

	'&::before': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		backgroundColor: token('color.background.neutral', colors.N30),
	},

	'&::after': {
		backgroundImage: `
        linear-gradient(
            90deg,
${/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766 */ ''}
            ${token('elevation.surface', colors.N0)} 0,
            ${token('color.skeleton.subtle', 'rgba(255, 255, 255, 0.2)')} 20%,
            ${token('color.skeleton.subtle', 'rgba(255, 255, 255, 0.5)')} 60%,
${/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766 */ ''}
            ${token('elevation.surface', colors.N0)}
        )
        `,
		transform: 'translateX(-100%)',
		animation: `${shimmer} 2s infinite`,
		position: 'absolute',
		top: 'space.0',
	},
});

const loadingAnimation = {
	'::before, ::after': {
		content: ' ',
		width: '100%',
		height: '100%',
		display: 'block',
	},

	'::before': {
		backgroundColor: token('color.background.neutral', colors.N30),
	},

	'::after': {
		backgroundImage: `
        linear-gradient(
            90deg,
            ${token('elevation.surface', colors.N0)} 0,
            ${token('color.skeleton.subtle', 'rgba(255, 255, 255, 0.2)')} 20%,
            ${token('color.skeleton.subtle', 'rgba(255, 255, 255, 0.5)')} 60%,
            ${token('elevation.surface', colors.N0)}
        )
        `,
		transform: 'translateX(-100%)',
		animation: `${shimmer} 2s infinite`,
		position: 'absolute',
		top: 0,
	},
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const LoadingItem = styled.div<{ backgroundColor: string }>({
	height: 'calc(100% - 8px)',
	width: '100%',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	backgroundColor: ({ backgroundColor }) => backgroundColor,
	position: 'relative',
	overflow: 'hidden',
	borderRadius: token('border.radius', '3px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	...loadingAnimation,
});

LoadingItem.displayName = 'LoadingItem';
